import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

const SoValidate = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [responseData, setResponseData] = useState(null)
  const [itemCount, setItemCount] = useState(0)
  const [isValidating, setIsValidating] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [editedData, setEditedData] = useState([])

  useEffect(() => {
    // Get data from navigation state
    if (location.state && location.state.responseData) {
      const data = location.state.responseData
      console.log("data", data)
      setResponseData(data)
      setItemCount(location.state.itemCount || 0)
      // Initialize editedData with the original data
      if (data.data && data.data.length > 0) {
        setEditedData([...data.data])
        // Trigger API call to match zoho items
        matchZohoItems(data.data)
      }
    } else {
      // If no data, redirect back to SoConverter
      toast.error('No data available. Please upload a PDF first.')
      navigate('/soConverter')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate])

  // Match zoho items API call
  const matchZohoItems = async (data) => {
    setIsValidating(true)
    try {
      const payload = {
        action: "match-zoho-items",
        data: data
      }

      const response = await axios.post(API_URL, payload)

      // Parse the response - handle both string and object responses
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      // Check if API call was successful
      if (responseData.success === true && responseData.data) {
        
        // Preserve original PDF prices before updating with validated data
        // The API response might update itemPrice with Zoho rate, but we want to show original PDF price
        const updatedData = responseData.data.map((validatedItem, index) => {
          // Find original item to preserve original itemPrice
          const originalItem = data[index]
          if (originalItem && originalItem.itemPrice && validatedItem.matchStatus === 'price_mismatch') {
            // Keep the original PDF itemPrice, don't overwrite with Zoho rate
            return {
              ...validatedItem,
              itemPrice: originalItem.itemPrice // Preserve original PDF price
            }
          }
          return validatedItem
        })
        
        // Update editedData with the validated data (with preserved PDF prices)
        setEditedData([...updatedData])
        // Update responseData message
        setResponseData(prev => ({
          ...prev,
          message: responseData.message,
          summary: responseData.summary
        }))
        // toast.success(responseData.message || "Validation completed", { autoClose: 2000 })
      } else {
        toast.error(responseData.message || "Validation failed")
      }
    } catch (error) {
      console.error("Match zoho items error:", error)
      if (error.response && error.response.data) {
        const errorData = typeof error.response.data === 'string' 
          ? JSON.parse(error.response.data) 
          : error.response.data
        toast.error(errorData.message || "Error validating items")
      } else {
        toast.error("Network error. Please try again.")
      }
    } finally {
      setIsValidating(false)
    }
  }

  // Handle input change for editable fields
  const handleInputChange = (itemIndex, key, value) => {
    const updatedData = [...editedData]
    updatedData[itemIndex] = {
      ...updatedData[itemIndex],
      [key]: value
    }
    setEditedData(updatedData)
  }

  // Format key name for display (convert camelCase to Title Case)
  const formatKeyName = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim()
  }

  // Convert date string to YYYY-MM-DD format for HTML date input
  const convertDateToInputFormat = (dateString) => {
    if (!dateString || dateString === '-' || dateString === '') {
      return ''
    }
    
    // Handle different date formats
    // Format: dd/mm/yyyy or dd-mm-yyyy
    const dateMatch = dateString.match(/(\d{2})[/-](\d{2})[/-](\d{4})/)
    if (dateMatch) {
      const [, day, month, year] = dateMatch
      return `${year}-${month}-${day}`
    }
    
    // If already in YYYY-MM-DD format, return as is
    const isoMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
      return dateString
    }
    
    // Try to parse as Date object
    try {
      const date = new Date(dateString)
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
    } catch (e) {
      // If parsing fails, return original value
    }
    
    return dateString
  }

  // Convert date from YYYY-MM-DD format back to dd-mm-yyyy format for display/storage
  const convertDateFromInputFormat = (dateString, originalFormat) => {
    if (!dateString || dateString === '') {
      return ''
    }
    
    // If it's already in the original format (dd/mm/yyyy or dd-mm-yyyy), return as is
    if (dateString.match(/(\d{2})[/-](\d{2})[/-](\d{4})/)) {
      return dateString
    }
    
    // Convert from YYYY-MM-DD to dd-mm-yyyy
    const dateMatch = dateString.match(/(\d{4})-(\d{2})-(\d{2})/)
    if (dateMatch) {
      const [, year, month, day] = dateMatch
      return `${day}-${month}-${year}`
    }
    
    return dateString
  }

  // Get value type to determine input type
  const getInputType = (key, value) => {
    if (key.toLowerCase().includes('date')) {
      return 'date'
    }
    if (key.toLowerCase().includes('email')) {
      return 'email'
    }
    if (typeof value === 'number' || key.toLowerCase().includes('quantity') || key.toLowerCase().includes('price') || key.toLowerCase().includes('rate') || key.toLowerCase().includes('discount') || key.toLowerCase().includes('percent')) {
      return 'number'
    }
    return 'text'
  }
  
  // Strip currency prefix from value (e.g., "INR 1000.00" -> "1000.00")
  const stripCurrencyPrefix = (value) => {
    if (!value || value === '') return ''
    const strValue = String(value)
    // Remove common currency prefixes
    return strValue.replace(/^(INR|USD|EUR|GBP|JPY)\s*/i, '').trim()
  }

  // Get display value based on input type
  const getDisplayValue = (key, value) => {
    if (key.toLowerCase().includes('date')) {
      return convertDateToInputFormat(value)
    }
    // Handle empty values and ensure proper string conversion
    if (value === null || value === undefined || value === '') {
      return ''
    }
    const stringValue = String(value)
    
    // For number inputs (price, rate, quantity, etc.), strip currency prefix
    const inputType = getInputType(key, stringValue)
    if (inputType === 'number') {
      return stripCurrencyPrefix(stringValue)
    }
    
    return stringValue
  }
  
  // Handle input change with proper date conversion
  const handleInputChangeWithConversion = (itemIndex, key, value, originalValue) => {
    if (key.toLowerCase().includes('date')) {
      // For date fields, value is already in YYYY-MM-DD from the input
      // Convert it back to dd-mm-yyyy format for storage
      const convertedValue = convertDateFromInputFormat(value, originalValue)
      handleInputChange(itemIndex, key, convertedValue)
    } else {
      handleInputChange(itemIndex, key, value)
    }
  }

  // Check if there are any price mismatches
  const hasPriceMismatch = () => {
    return editedData.some(item => item.matchStatus === 'price_mismatch')
  }

  // Map API field names to CSV column names (exact names as shown in image)
  const getCSVColumnName = (fieldName) => {
    const columnMap = {
      'orderDate': 'Order Date',
      'expectedShipmentDate': 'Expected Shipment Date',
      'salesOrderNumber': 'SalesOrder Number',
      'status': 'Status',
      'customerName': 'Customer Name',
      'purchaseOrder': 'PurchaseOrder',
      'templateName': 'Template Name',
      'currencyCode': 'Currency Code',
      'placeOfSupply': 'Place of Supply ',
      'gstTreatment': 'GST Treatment',
      'gstRegistrationNumber': 'GST Identification Number (GSTIN)',
      'exchangeRate': 'Exchange Rate',
      'discountType': 'Discount Type',
      'isDiscountBeforeTax': 'Is Discount Before Tax',
      'entityDiscountPercent': 'Entity Discount Percent',
      'itemName': 'Item Name',
      'hsn': 'HSN/SAC',
      'sku': 'SKU',
      'itemDesc': 'Item Desc',
      'quantity': 'Quantity',
      'usageUnit': 'Usage unit',
      'warehouseName': 'Warehouse Name',
      'itemPrice': 'Item Price',
      // 'netPrice': 'Net Price',
      'Is Inclusive Tax': 'Is Inclusive Tax',
      'Discount': 'Discount',
      'Item Tax': 'Item Tax',
      'Item Tax %': 'Item Tax %',
      'Item Tax Type': 'Item Tax Type',
      'Item Tax Exemption Reason': 'Item Tax Exemption Reason',
      'Item Type': 'Item Type',
      'Project Name': 'Project Name',
      'Shipping Charge Tax Name': 'Shipping Charge Tax Name',
      'Shipping Charge Tax Type': 'Shipping Charge Tax Type',
      'Shipping Charge Tax %': 'Shipping Charge Tax %',
      'Shipping Charge': 'Shipping Charge',
      'Shipping Charge Tax Exemption Code': 'Shipping Charge Tax Exemption Code',
      'Shipping Charge SAC Code': 'Shipping Charge SAC Code',
      'Adjustment': 'Adjustment',
      'Adjustment Description': 'Adjustment Description',
      'Reverse Charge Tax Name': 'Reverse Charge Tax Name',
      'Reverse Charge Tax Rate': 'Reverse Charge Tax Rate',
      'Reverse Charge Tax Type': 'Reverse Charge Tax Type',
      'Supply Type': 'Supply Type',
      'Sales person': 'Sales person',
      'Notes': 'Notes',
      'Terms & Conditions': 'Terms & Conditions',
      'Delivery Method': 'Delivery Method'
    }
    return columnMap[fieldName] || fieldName
  }

  // Get CSV column order (as shown in the image)
  const getCSVColumnOrder = () => {
    return [
      'orderDate',                    // Order Date
      'expectedShipmentDate',          // Expected S (Expected Shipment Date)
      'salesOrderNumber',              // SalesOrder (Sales Order Number)
      'status',                        // Status
      'customerName',                  // Customer
      'purchaseOrder',                 // Purchased (Purchase Order)
      'templateName',                  // Template
      'currencyCode',                  // Currency C (Currency Code)
      'placeOfSupply',                 // Place of S (Place of Supply)
      'gstTreatment',                  // GST Treat: (GST Treatment)
      'gstRegistrationNumber',         // GST Identil (GST Identical/Registration Number)
      'exchangeRate',                  // Exchange I (Exchange Rate)
      'discountType',                  // Discount I (Discount Type)
      'isDiscountBeforeTax',           // IsDiscoun (Is Discount Before Tax)
      'entityDiscountPercent',        // EntityDisco (Entity Discount Percent)
      'itemName',                      // Item Name
      'hsn',                          // HSN/SAC
      'sku',                          // SKU
      'itemDesc',                     // Item Desc: (Item Description)
      'quantity',                     // Quantity
      'usageUnit',                    // Usage unit (Usage Unit)
      'warehouseName',                // Warehous (Warehouse)
      'itemPrice',                    // Item Price
      'Is Inclusive Tax',             // Is Inclusive
      'Discount',                      // Discount
      'Item Tax',                     // Item Tax
      'Item Tax %',                   // Item Tax %
      'Item Tax Type',                // Item Tax Ty (Item Tax Type)
      'Item Tax Exemption Reason',    // Item Tax E (Item Tax Exemption Reason)
      'Item Type',                    // Item type (Item Type)
      'Project Name',                 // Project No (Project Name)
      'Shipping Charge Tax Name',     // Shipping C (1st - Tax Name)
      'Shipping Charge Tax Type',     // Shipping C (2nd - Tax Type)
      'Shipping Charge Tax %',        // Shipping C (3rd - Tax %)
      'Shipping Charge',              // Shipping C (4th - Shipping Charge)
      'Shipping Charge Tax Exemption Code', // Shipping C (5th - Tax Exemption Code)
      'Shipping Charge SAC Code',     // Shipping C (6th - SAC Code - if exists)
      'Adjustment',                    // Adjustmen (1st - Adjustment)
      'Adjustment Description',       // Adjustmen (2nd - Adjustment Description)
      'Reverse Charge Tax Name',      // Reverse Cl (1st - Tax Name)
      'Reverse Charge Tax Rate',      // Reverse Cl (2nd - Tax Rate)
      'Reverse Charge Tax Type',      // Reverse Cl (3rd - Tax Type)
      'Supply Type',                  // Supply Typ (Supply Type)
      'Sales person',                 // Salesperse (Salesperson)
      'Notes',                        // Notes
      'Terms & Conditions',            // Terms & C (Terms & Conditions)
      'Delivery Method'               // Delivery Method
    ]
  }

  // Convert array of objects to CSV
  const convertToCSV = (data) => {
    if (!data || data.length === 0) {
      return ''
    }

    // Get all available keys from data
    const availableKeys = new Set()
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        // Exclude internal fields like zohoData, matchStatus, message
        if (key !== 'zohoData' && key !== 'matchStatus' && key !== 'message') {
          availableKeys.add(key)
        }
      })
    })

    // Get ordered column list (only include columns that exist in data)
    const columnOrder = getCSVColumnOrder()
    const headers = columnOrder.filter(key => availableKeys.has(key))
    
    // Exclude attached/unnecessary data - only include fields in the predefined column order
    // Do not add any remaining keys that weren't in the ordered list
    
    // Create CSV header row with exact column names
    const headerRow = headers.map(header => {
      const csvColumnName = getCSVColumnName(header)
      // Escape commas and quotes in header
      return `"${csvColumnName.replace(/"/g, '""')}"`
    }).join(',')

    // Create CSV data rows
    const dataRows = data.map(item => {
      return headers.map(header => {
        const value = item[header]
        // Handle null, undefined, and empty values
        const stringValue = value !== null && value !== undefined ? String(value) : ''
        // Escape commas, quotes, and newlines in value
        return `"${stringValue.replace(/"/g, '""').replace(/\n/g, ' ').replace(/\r/g, '')}"`
      }).join(',')
    })

    // Combine header and data rows
    return [headerRow, ...dataRows].join('\n')
  }

  // Export data to CSV via API
  const exportToCSV = async () => {
    setIsExporting(true)
    try {
      // Prepare data for export (remove internal fields)
      const exportData = editedData.map(item => {
        const { zohoData, matchStatus, message, ...cleanItem } = item
        return cleanItem
      })

      // Get userid from localStorage
      const userId = localStorage.getItem('id')
      if (!userId) {
        toast.error("User session expired. Please login again.", { autoClose: 2000 })
        setIsExporting(false)
        return
      }

      const payload = {
        action: "export-so-data",
        data: exportData,
        userid: userId
      }

      const response = await axios.post(API_URL, payload)

      // Parse the response - handle both string and object responses
      let responseData = response.data
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      // Check if API call was successful
      if (responseData.success === true) {
        // If API returns CSV content directly
        if (responseData.csvContent) {
          // Create blob and download
          const blob = new Blob([responseData.csvContent], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          const url = URL.createObjectURL(blob)
          
          link.setAttribute('href', url)
          link.setAttribute('download', responseData.fileName || `so_validate_${new Date().toISOString().split('T')[0]}.csv`)
          link.style.visibility = 'hidden'
          
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          // toast.success(responseData.message || 'Data exported to CSV successfully', { autoClose: 2000 })
        } else {
          // If API doesn't return CSV, generate it client-side
          const csvContent = convertToCSV(exportData)
          
          // Create blob and download
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          const url = URL.createObjectURL(blob)
          
          link.setAttribute('href', url)
          link.setAttribute('download', `so_validate_${new Date().toISOString().split('T')[0]}.csv`)
          link.style.visibility = 'hidden'
          
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          toast.success(responseData.message || 'Data exported to CSV successfully', { autoClose: 2000 })
        }
      } else {
        toast.error(responseData.message || 'Failed to export data', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Export error:', error)
      if (error.response && error.response.data) {
        const errorData = typeof error.response.data === 'string' 
          ? JSON.parse(error.response.data) 
          : error.response.data
        toast.error(errorData.message || 'Failed to export data to CSV', { autoClose: 2000 })
      } else {
        toast.error('Network error. Please try again.', { autoClose: 2000 })
      }
    } finally {
      setIsExporting(false)
    }
  }

  // Handle export button click
  const handleExport = () => {
    if (editedData.length === 0) {
      toast.error('No data to export', { autoClose: 2000 })
      return
    }

    // Check if there are any price mismatches
    if (hasPriceMismatch()) {
      // Show confirmation dialog
      const confirmed = window.confirm(
        'Are you sure you want to export the data? Item price is Mismatch.'
      )
      
      if (confirmed) {
        exportToCSV()
      }
    } else {
      // No price mismatch, export directly
      exportToCSV()
    }
  }

  if (!responseData || !responseData.data || responseData.data.length === 0) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0 txt_lft">SO Validate</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body text-center">
                    <PulseLoader color="#007bff" size={10} />
                    <p className="mt-3">Loading data...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const data = responseData.data

  // Get all unique keys from all items
  const getAllKeys = () => {
    const keysSet = new Set()
    data.forEach(item => {
      Object.keys(item).forEach(key => keysSet.add(key))
    })
    return Array.from(keysSet)
  }

  const allKeys = getAllKeys()

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">SO Validate</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              <button
                className="btn btn-blue mr-2"
                onClick={handleExport}
                disabled={isValidating || editedData.length === 0 || isExporting}
              >
                {isExporting ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <PulseLoader color="#fff" size={5} style={{ marginRight: '5px' }} />
                    Exporting...
                  </span>
                ) : (
                  <>
                    <i className="fa fa-download mr-1" /> Export
                  </>
                )}
              </button>
              <button
                className="btn btn-blue"
                onClick={() => navigate('/soConverter')}
              >
                Back to Converter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-lg-12">
              <div className="card card-primary card-outline">
                <div className="card-header">
                  <h3 className="card-title">
                    Processed Items: {itemCount}
                  </h3>
                  {/* {responseData.message && (
                    <div className="card-tools">
                      <span className="badge badge-info">{responseData.message}</span>
                    </div>
                  )} */}
                  {responseData.summary && (
                    <div className="card-tools mt-2">
                      <span className="badge badge-success mr-1">Matched: {responseData.summary.matched || 0}</span>
                      <span className="badge badge-warning mr-1">Price Mismatch: {responseData.summary.priceMismatch || 0}</span>
                      <span className="badge badge-danger">Not Found: {responseData.summary.notFound || 0}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Editable Key-Value Pairs for each item */}
          {isValidating ? (
            <div className="row">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body text-center">
                    <PulseLoader color="#007bff" size={10} />
                    {/* <p className="mt-3">Validating items with Zoho...</p> */}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
              {editedData.map((item, itemIndex) => {
                const isPriceMismatch = item.matchStatus === 'price_mismatch'
                // Get Zoho rate from zohoData or rate field - check multiple possible locations
                let zohoRate = ''
                if (item.zohoData && item.zohoData.rate) {
                  zohoRate = String(item.zohoData.rate)
                } else if (item.rate) {
                  zohoRate = String(item.rate)
                }
                
                // Strip currency prefix from zohoRate for display
                const displayZohoRate = zohoRate ? stripCurrencyPrefix(zohoRate) : ''
                
                // Debug logging
                if (isPriceMismatch) {
                  console.log('Item', itemIndex, 'Price Mismatch:', {
                    itemPrice: item.itemPrice,
                    zohoData: item.zohoData,
                    rate: item.rate,
                    zohoRate: zohoRate,
                    displayZohoRate: displayZohoRate
                  })
                }
                
                return (
                  <div key={itemIndex} className="row mb-4">
                    <div className="col-lg-12">
                      <div className="card card-primary card-outline">
                        <div className="card-header">
                          <h3 className="card-title">Item {itemIndex + 1}</h3>
                          {item.matchStatus && (
                            <div className="card-tools">
                              <span className={`badge ${
                                item.matchStatus === 'matched' ? 'badge-success' :
                                item.matchStatus === 'price_mismatch' ? 'badge-warning' :
                                'badge-danger'
                              }`}>
                                {item.matchStatus === 'matched' ? 'Matched' :
                                 item.matchStatus === 'price_mismatch' ? 'Price Mismatch' :
                                 'Not Found'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {allKeys.map((key, keyIndex) => {
                              const originalValue = item[key] !== null && item[key] !== undefined ? String(item[key]) : ''
                              const inputType = getInputType(key, originalValue)
                              const displayValue = getDisplayValue(key, originalValue)
                              const isPriceField = key.toLowerCase() === 'itemprice' || key.toLowerCase() === 'price' || key.toLowerCase() === 'rate'
                              const showZohoRate = isPriceField && isPriceMismatch && displayZohoRate && (key.toLowerCase() === 'itemprice' || key.toLowerCase() === 'price')
                              
  return (
                                <div key={keyIndex} className="col-lg-6 col-md-6 col-sm-12 mb-3">
                                  <div className="form-group row align-items-center">
                                    <label className="col-12 col-md-4 col-form-label" style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>
                                      {formatKeyName(key)}:
                                    </label>
                                    <div className="col-12 col-md-8">
                                      {inputType === 'date' ? (
                                        <div className="input-group">
                                          <input
                                            type="date"
                                            className="form-control"
                                            value={displayValue}
                                            onChange={(e) => handleInputChangeWithConversion(itemIndex, key, e.target.value, originalValue)}
                                            placeholder="dd-mm-yyyy"
                                          />
                                          <div className="input-group-append">
                                            <div className="input-group-text">
                                              <i className="fa fa-calendar" />
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <input
                                          type={inputType}
                                          className={`form-control ${isPriceField && isPriceMismatch ? 'border-danger' : ''}`}
                                          value={displayValue}
                                          onChange={(e) => handleInputChange(itemIndex, key, e.target.value)}
                                          placeholder={`Enter ${formatKeyName(key)}`}
                                        />
                                      )}
                                      {showZohoRate && (
                                        <div className="mt-2">
                                          <label className="text-muted" style={{ fontSize: '0.875rem', fontWeight: '500' }}>Zoho Rate:</label>
                                          <input
                                            type="number"
                                            className="form-control border-danger"
                                            value={displayZohoRate}
                                            readOnly
                                            style={{ backgroundColor: '#fff5f5' }}
                                          />
                                          <small className="text-danger mt-1 d-block">
                                            {item.message || 'Price mismatch detected'}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SoValidate
